import RightPanelCard from "components/CommonComponents/RightPanelCard";
import DOMPurify from "dompurify";
import {
  SELECT_CUSTOM_STYLES,
  LessonTypeColor,
  REGEX,
} from "constants/CommonConstants";
import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Collapse,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
  CardFooter,
  Modal,
  ModalHeader,
  ModalBody,
  PopoverBody,
  UncontrolledPopover,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import Select from "react-select";
import { COURSE_TYPE, MODULES, LESSON_TYPE_META } from "components/constant";
import ReactQuill from "react-quill";
import * as XLSX from "xlsx";
import { renderAsync } from "docx-preview";
import {
  getLessonsByCourseId,
  getLessonById,
  updateLessonById,
  deleteLesson,
  addLesson,
} from "action/lesson";
import { resetLessonList } from "constants/lesson";
import { useDispatch, useSelector } from "react-redux";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import { MAX_LESSON_SIZE, MAX_DESCRIPTION_LENGTH, OFFICE_PREVIEW_URL, PREVIEW_LIMIT} from "constants/CommonConstants";
import {  } from "constants/CommonConstants";


// Quill plain-text helpers — single source of truth for length.
const getQuillPlainText = (editor) => {
  const t = editor.getText() || "";
  return t.endsWith("\n") ? t.slice(0, -1) : t;
};

const getQuillPlainLength = (editor) => getQuillPlainText(editor).length;

const initialFields = {
  id: "",
  lessonName: {
    value: "",
    oldValue: "",
    error: "",
  },
  description: {
    value: "",
    oldValue: "",
    error: "",
  },
  lessonDocumentType: {
    value: "",
    oldValue: "",
    error: "",
  },
  duration: {
    value: "",
    oldValue: "",
    error: "",
  },
  lessonSeqNo: {
    value: "",
    oldValue: "",
    error: "",
  },
  language: {
    value: "",
    oldValue: "",
    error: "",
  },
  lessonFiles: {
    value: [],
    oldValue: [],
    error: "",
  },
};

const ACCEPTED_FORMATS_MAP = {
  IMAGE: {
    label: "PNG, JPG, JPEG",
    accept: "image/*",
  },
  PDF: {
    label: "PDF",
    accept: "application/pdf",
  },
  VIDEO: {
    label: "MP4, MOV",
    accept: "video/mp4,video/quicktime",
  },
  DOCUMENT: {
    label: "DOCX",
    accept:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  SHEET: {
    label: "XLSX",
    accept:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  },
};

const LessonsSection = ({
  open,
  toggle,
  selectedLanguages = [],
  onAddLessonClick,
  courseId,
  courseType,
}) => {
  const { notify } = useContext(NotificationContext);
  const dispatch = useDispatch();
  const docxContainerRef = useRef(null);
  const quillRef = useRef(null);
  const [lessonFormData, setLessonFormData] = useState(initialFields);
  const [isModalOpen, setIsModalOpen] = useState(null); //"add "edit" "null"
  const [activeLanguageTab, setActiveLanguageTab] = useState("ENGLISH");
  const [descriptionLength, setDescriptionLength] = useState(0);
  const [showAlternative, setShowAlternative] = useState({
    loader: false,
    displayedLessonsLoader: false,
    lessonsLoader: false,
    error: "",
  });
  const [previewData, setPreviewData] = useState({
    src: "",
    width: 0,
    height: 0,
  });
  // Inline seq-number editing state
  const [lessonUI, setLessonUI] = useState({
    seqEdit: { id: null, value: "" },
    excelData: [],
    previewType: "",
    savingSeqId: null,
  });
  // Track which lesson cards have expanded descriptions
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const lessonListData = useSelector((state) => state.lesson.lessonList);

  const [isQuillMounted, setIsQuillMounted] = useState(false);

const handleQuillRef = useCallback((instance) => {
  quillRef.current = instance;
  setIsQuillMounted(!!instance);
}, []);

  useEffect(() => {
    if (courseId) {
      fetchLessons();
    } else {
      // No courseId means this is a fresh Add Course — clear any stale lesson
      // data left in Redux from the previously visited course.
      dispatch({ type: resetLessonList });
    }
  }, [courseId]);

  useEffect(() => {
    if (!lessonListData) return;

    if (lessonListData?.code === 200) {
      setShowAlternative((prev) => ({
        ...prev,
        lessonsLoader: false,
        error: "",
      }));
    } else if (lessonListData?.code || lessonListData?.message) {
      setShowAlternative((prev) => ({
        ...prev,
        lessonsLoader: false,
        error: lessonListData?.message || "Failed to fetch lessons",
      }));

      notify("danger", lessonListData?.message || "Failed to fetch lessons");
    }
  }, [lessonListData]);

  // -------------------------------------------------------------
// Lesson description — length guard + paste guard + safety net
// -------------------------------------------------------------
useEffect(() => {
  if (!quillRef.current) return;

  const editor = quillRef.current.getEditor();

  const getVisibleLength = () => getQuillPlainLength(editor);

  // -------- KEYDOWN --------
  const handleKeyDown = (e) => {
    if (
      e.ctrlKey ||
      e.metaKey ||
      e.altKey ||
      e.key === "Backspace" ||
      e.key === "Delete" ||
      e.key.startsWith("Arrow") ||
      e.key === "Tab" ||
      e.key === "Home" ||
      e.key === "End" ||
      e.key === "PageUp" ||
      e.key === "PageDown"
    ) {
      return;
    }

    const currentLength = getVisibleLength();
    const selection = editor.getSelection();
    const selectedLength = selection?.length || 0;

    if (e.key.length === 1) {
      const netAdded = 1 - selectedLength;
      if (currentLength + netAdded > MAX_DESCRIPTION_LENGTH) {
        e.preventDefault();
      }
      return;
    }

    if (e.key === "Enter") {
      if (currentLength + 1 - selectedLength > MAX_DESCRIPTION_LENGTH) {
        e.preventDefault();
      }
    }
  };

  // -------- PASTE --------
  const handlePaste = (e) => {
    const pastedText =
      e.clipboardData?.getData("text/plain") ||
      e.clipboardData?.getData("text") ||
      "";

    if (!pastedText) return;

    const selection = editor.getSelection() || {
      index: getVisibleLength(),
      length: 0,
    };

    const remaining =
      MAX_DESCRIPTION_LENGTH - (getVisibleLength() - selection.length);

    if (remaining <= 0) {
      e.preventDefault();
      return;
    }

    // Normalize Windows line endings so length math matches Quill's
    // internal representation (getText returns \n, not \r\n).
    const normalizedPaste = pastedText
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n");

    if (normalizedPaste.length <= remaining) {
      // Fits — let Quill handle it natively (preserves formatting)
      return;
    }

    // Doesn't fit — truncate and insert as plain text
    e.preventDefault();
    editor.deleteText(selection.index, selection.length);
    editor.insertText(
      selection.index,
      normalizedPaste.substring(0, remaining),
    );
  };

  // -------- TEXT CHANGE (safety net) --------
  const handleTextChange = (delta, oldDelta, source) => {
    // Update the counter on every change (user or api)
    setDescriptionLength(getVisibleLength());

    if (source !== "user") return;

    const len = getVisibleLength();
    if (len <= MAX_DESCRIPTION_LENGTH) return;

    const overflow = len - MAX_DESCRIPTION_LENGTH;
    const end = editor.getLength() - 1;
    editor.deleteText(end - overflow, overflow, "silent");
  };

  editor.root.addEventListener("keydown", handleKeyDown);
  editor.root.addEventListener("paste", handlePaste);
  editor.on("text-change", handleTextChange);

  // Sync counter when the modal opens (editor value is set via `value` prop)
  setDescriptionLength(getVisibleLength());

  return () => {
    editor.root.removeEventListener("keydown", handleKeyDown);
    editor.root.removeEventListener("paste", handlePaste);
    editor.off("text-change", handleTextChange);
  };
}, [isModalOpen, isQuillMounted]);

  useEffect(() => {
    const renderDocx = async () => {
      try {
        if (lessonUI.previewType !== "docx") return;

        const lessonFile = lessonFormData.lessonFiles.value[0];

        if (!lessonFile) return;

        let buffer;

        if (lessonFile.file) {
          // Add mode
          buffer = await lessonFile.file.arrayBuffer();
        } else if (lessonFile.filePath) {
          // Edit mode
          const response = await fetch(lessonFile.filePath);

          if (!response.ok) {
            throw new Error("Failed to fetch document");
          }

          buffer = await response.arrayBuffer();
        } else {
          return;
        }

        if (docxContainerRef.current) {
          docxContainerRef.current.innerHTML = "";
          await renderAsync(buffer, docxContainerRef.current);
        }
      } catch (error) {
        notify("danger", "Unable to preview this DOCX file");
        setLessonUI((prev) => ({ ...prev, previewType: "" }));
      }
    };

    renderDocx();
  }, [lessonUI.previewType, lessonFormData.lessonFiles.value]);

  const hasEnglish = selectedLanguages?.some(
    (lang) => lang.value === "ENGLISH",
  );

  const hasHindi = selectedLanguages?.some((lang) => lang.value === "HINDI");

  const isHindi = activeLanguageTab === "HINDI";

  useEffect(() => {
    if (hasHindi && !hasEnglish) {
      setActiveLanguageTab("HINDI");
    } else if (hasEnglish && !hasHindi) {
      setActiveLanguageTab("ENGLISH");
    } else if (!hasEnglish && !hasHindi) {
      setActiveLanguageTab("ENGLISH");
    }
  }, [hasEnglish, hasHindi]);

  const fetchLessons = async () => {
    try {
      setShowAlternative((prev) => ({
        ...prev,
        lessonsLoader: true,
      }));

      const res = await getLessonsByCourseId(courseId, dispatch);
    } catch (error) {
      notify("danger", error?.message || "Failed to fetch lessons2");
    } finally {
      setShowAlternative((prev) => ({
        ...prev,
        lessonsLoader: false,
      }));
    }
  };

  const openLessonPreview = async (lesson) => {
    const fileUrl =
      lesson.documents?.[0]?.file_path || lesson.documents?.[0]?.filePath;

    if (!fileUrl) {
      notify("warning", "No document available for preview");
      return;
    }

    const extension = fileUrl.split(".").pop()?.toLowerCase();

    if (["xls", "xlsx", "doc", "docx"].includes(extension)) {
      setPreviewData({
        src: `${OFFICE_PREVIEW_URL}${encodeURIComponent(fileUrl)}`,
        width: 0,
        height: 0,
        // fileName: fileUrl.split("?")[0].split("#")[0].split("/").pop(),                //we may require this in future if we want to show the file name in the preview modal
      });
      setLessonUI((prev) => ({
        ...prev,
        previewType: "default",
      }));
      return;
    }

    setPreviewData({
      src: fileUrl,
      width: 0,
      height: 0,
      // fileName: fileUrl.split("?")[0].split("#")[0].split("/").pop(),                     //we may require this in future if we want to show the file name in the preview modal
    });

    setLessonUI((prev) => ({
      ...prev,
      previewType: "default",
    }));
  };

  const getFileIcon = (fileName = "") => {
    const extension = fileName.split(".").pop()?.toLowerCase();

    if (["pdf"].includes(extension))
      return { icon: "fa-file-pdf", color: "text-danger" };

    if (["doc", "docx"].includes(extension))
      return { icon: "fa-file-word", color: "text-primary" };

    if (["xls", "xlsx", "csv"].includes(extension))
      return { icon: "fa-file-excel", color: "text-success" };

    if (["ppt", "pptx"].includes(extension))
      return { icon: "fa-file-powerpoint", color: "text-warning" };

    return { icon: "fa-file", color: "text-muted" };
  };

  const openAddModal = async () => {
    const canProceed = await onAddLessonClick?.();

    if (!canProceed) return;

    const displayLessons = isHindi
      ? lessonListData?.data?.hindiLessons || []
      : lessonListData?.data?.englishLessons || [];
    
    let maxSeq = 0;
    if (displayLessons.length > 0) {
      maxSeq = Math.max(...displayLessons.map((lesson) => Number(lesson.lesson_seq_no) || 0));
    }
    const nextSeq = String(maxSeq + 1);

    setIsModalOpen("add");
    setLessonFormData({
      ...initialFields,
      lessonDocumentType: {
        value: courseType === "RECORDED" ? "VIDEO" : "",
        oldValue: "",
        error: "",
      },
      lessonSeqNo: {
        value: nextSeq,
        oldValue: "",
        error: "",
      },
    });
  };

  const closeAddModal = () => {
  setIsModalOpen(null);
  setLessonFormData({ ...initialFields });
  setDescriptionLength(0);
};

  const handleInputChange = (field, value) => {
    setLessonFormData((prev) => {
      let newState = {
        ...prev,
        [field]: { value, error: "" },
      };

      if (field === "lessonDocumentType" && value) {
        const fileObj = prev.lessonFiles?.value?.[0];
        if (fileObj && !fileObj.isExisting) {
          const file = fileObj.file;
          const contentType = file.type;
          const fileSize = file.size;

          let hasError = false;
          let errorMsg = "";

          switch (value) {
            case "IMAGE":
              if (
                ![
                  "image/jpeg",
                  "image/jpg",
                  "image/png",
                ].includes(contentType)
              ) {
                hasError = true;
                errorMsg = "Image files must be JPG, JPEG or PNG.";
              }
              break;
            case "VIDEO":
              if (fileSize > MAX_LESSON_SIZE) {
                hasError = true;
                errorMsg = "video file size must not exceed 1 GB";
              } else if (
                ![
                  "video/mp4",
                  "video/quicktime",
                ].includes(contentType)
              ) {
                hasError = true;
                errorMsg = "video files must be mp4 or mov";
              }
              break;
            case "PDF":
              if (contentType !== "application/pdf") {
                hasError = true;
                errorMsg = "only PDF files are allowed";
              }
              break;
            case "DOCUMENT":
              if (
                ![
                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                ].includes(contentType)
              ) {
                hasError = true;
                errorMsg = "document files must be docx";
              }
              break;
            case "SHEET":
              if (
                ![
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  "text/csv",
                ].includes(contentType)
              ) {
                hasError = true;
                errorMsg = "sheet files must be xlsx";
              }
              break;
            case "URL":
              hasError = true;
              errorMsg = "URL document type does not support file upload";
              break;
          }

          if (hasError) {
            notify("danger", errorMsg);
            newState.lessonFiles = {
              ...prev.lessonFiles,
              value: [],
              error: errorMsg,
            };
          } else {
            newState.lessonFiles = {
              ...prev.lessonFiles,
              error: "",
            };
          }
        }
      }

      return newState;
    });
  };

  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setLessonFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const handleEditLesson = async (lesson) => {
    try {
      setShowAlternative((prev) => ({
        ...prev,
        loader: true,
      }));

      const res = await getLessonById(lesson.id);

      if (res?.code === 200) {
        const lessonData = res.data;

        setLessonFormData({
          id: lessonData.id,

          lessonName: {
            value: lessonData.lesson_name || "",
            oldValue: lessonData.lesson_name || "",
            error: "",
          },

          description: {
            value: lessonData.description || "",
            oldValue: lessonData.description || "",
            error: "",
          },

          lessonDocumentType: {
            value:
              courseType === "RECORDED"
                ? "VIDEO"
                : lessonData.lesson_document_type || "",
            oldValue:
              courseType === "RECORDED"
                ? "VIDEO"
                : lessonData.lesson_document_type || "",
            error: "",
          },

          duration: {
            value: lessonData.duration || "",
            oldValue: lessonData.duration || "",
            error: "",
          },

          lessonSeqNo: {
            value: lessonData.lesson_seq_no || "",
            oldValue: lessonData.lesson_seq_no || "",
            error: "",
          },

          language: {
            value: lessonData.language || "",
            oldValue: lessonData.language || "",
            error: "",
          },

          lessonFiles: {
            value:
              lessonData.documents?.map((doc) => ({
                id: doc.id,
                filePath: doc.file_path,
                preview: doc.file_path,
                isExisting: true,
              })) || [],
            oldValue:
              lessonData.documents?.map((doc) => ({
                id: doc.id,
                filePath: doc.file_path,
                preview: doc.file_path,
                isExisting: true,
              })) || [],
            error: "",
          },
        });

        setShowAlternative((prev) => ({
          ...prev,
          loader: false,
        }));

        setIsModalOpen("edit");
      } else {
        setShowAlternative((prev) => ({
          ...prev,
          loader: false,
        }));

        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to load lesson details";

        notify("danger", message);
      }
    } catch (error) {
      setShowAlternative((prev) => ({
        ...prev,
        loader: false,
        error: error?.message || "Something went wrong while fetching lesson",
      }));

      notify(
        "danger",
        error?.message || "Something went wrong while fetching lesson",
      );
    }
  };

  const validateForm = () => {
    const newLesson = { ...lessonFormData };

    Object.keys(newLesson).forEach((key) => {
      if (key === "id") return;

      newLesson[key] = {
        ...newLesson[key],
        error: getValidationError(key, newLesson[key].value),
      };
    });

    setLessonFormData(newLesson);

    return Object.entries(newLesson)
      .filter(([key]) => key !== "id")
      .every(([, item]) => item.error === "");
  };

  const getValidationError = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "lessonName":
        return !trimmedValue ? "Please enter lesson title" : "";

      case "lessonDocumentType":
        return !value ? "Please select lesson file upload  type" : "";

      case "lessonSeqNo":
        return !value
          ? "Please enter content sequence"
          : !REGEX.ONLY_DIGITS_REGEX.test(String(value).trim())
            ? "Content sequence must contain only digits"
            : Number(value) > 500
              ? "Please enter a content sequence that does not exceed 500"
              : "";

      // case "duration":
      //   return !value
      //     ? "Please enter lesson duration"
      //     : !REGEX.ONLY_DIGITS_REGEX.test(String(value).trim())
      //       ? "Lesson duration must contain only digits"
      //       : Number(value) > 500
      //         ? "Please enter a lesson duration that does not exceed 500"
      //         : "";

        case "duration":
          if (courseType === "RECORDED") {
            return !value ? "Video duration could not be determined" : "";
          }

          return !value
            ? "Please enter lesson duration"
            : !REGEX.ONLY_DIGITS_REGEX.test(String(value).trim())
              ? "Lesson duration must contain only digits"
              : Number(value) > 500
                ? "Please enter a lesson duration that does not exceed 500"
                : "";

          case "description": {
          const editor = quillRef.current?.getEditor();
          const textLen = editor ? getQuillPlainLength(editor) : 0;

          if (!textLen) {
            return "Please enter a lesson description";
          }

          if (textLen > MAX_DESCRIPTION_LENGTH) {
            return `Lesson description cannot exceed ${MAX_DESCRIPTION_LENGTH} characters.`;
          }

          return "";
        }

      case "lessonFiles":
        return !value || value.length === 0
          ? "Please upload the lesson file"
          : "";

      default:
        return "";
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setShowAlternative((prev) => ({
        ...prev,
        displayedLessonsLoader: true,
      }));
      let res;

      if (isModalOpen === "add") {
        const payload = {
          courseID: courseId,
          lessonSeqNo: Number(lessonFormData.lessonSeqNo.value),
          lessonName: lessonFormData.lessonName.value,
          lessonDocumentType: lessonFormData.lessonDocumentType.value,
          duration: Number(lessonFormData.duration.value),
          description: lessonFormData.description.value,
          language: activeLanguageTab,
        };

        const formData = new FormData();

        formData.append("lessonForm", JSON.stringify(payload));

        lessonFormData.lessonFiles.value.forEach((item) => {
          formData.append("lessonFiles", item.file);
        });

        res = await addLesson(formData);
      } else {
        const payload = {};

        Object.entries(lessonFormData).forEach(([key, field]) => {
          if (key === "id" || key === "lessonFiles") return;

          if (field.value !== field.oldValue) {
            payload[key] =
              key === "lessonSeqNo" || key === "duration"
                ? Number(field.value)
                : field.value;
          }
        });

        const newFile = lessonFormData.lessonFiles.value.find(
          (item) => !item.isExisting,
        );

        if (newFile) {
          payload.lessonDocumentType = lessonFormData.lessonDocumentType.value;
        }

        const formData = new FormData();

        formData.append("lessonForm", JSON.stringify(payload));

        lessonFormData.lessonFiles.value.forEach((item) => {
          if (!item.isExisting) {
            formData.append("lessonFiles", item.file);
          }
        });

        res = await updateLessonById(lessonFormData.id, formData);
      }

      if (res?.code === 200) {
        notify(
          "success",
          // res?.message ||
            (isModalOpen === "edit"
              ? "Lesson updated successfully"
              : "Lesson added successfully"),
        );

        await fetchLessons();
        closeAddModal();
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message ||
            (isModalOpen === "edit"
              ? "Failed to update lesson"
              : "Failed to add lesson");

        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message || "Something went wrong");
    } finally {
      setShowAlternative((prev) => ({
        ...prev,
        displayedLessonsLoader: false,
      }));
    }
  };

  const getVideoDuration = (file) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);

    video.preload = "metadata";

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);

      if (!Number.isFinite(video.duration)) {
        reject(new Error("Unable to determine video duration"));
        return;
      }

      resolve(video.duration);
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Unable to read video duration"));
    };

    video.src = url;
  });
};



  const handleAddFile = async (file) => {
  if (!file) return;

  const documentType = lessonFormData.lessonDocumentType?.value;
  const contentType = file.type;
  const fileSize = file.size;

  const setError = (msg) => {
    notify("danger", msg);

    setLessonFormData((prev) => ({
      ...prev,
      lessonFiles: {
        ...prev.lessonFiles,
        error: msg,
      },
    }));
  };

  // ============================================================
  // FILE VALIDATION
  // ============================================================

  if (documentType) {
    switch (documentType) {
      case "IMAGE":
        if (
          !["image/jpeg", "image/jpg", "image/png"].includes(contentType)
        ) {
          return setError("image files must be jpg, jpeg or png");
        }
        break;

      case "VIDEO":
        if (fileSize > MAX_LESSON_SIZE) {
          return setError("video file size must not exceed 1 GB");
        }

        if (
          ![
            "video/mp4",
            "video/quicktime",
          ].includes(contentType)
        ) {
          return setError("video files must be mp4 or mov");
        }

        break;

      case "PDF":
        if (contentType !== "application/pdf") {
          return setError("only PDF files are allowed");
        }
        break;

      case "DOCUMENT":
        if (
          ![
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          ].includes(contentType)
        ) {
          return setError("document files must be docx");
        }
        break;

      case "SHEET":
        if (
          ![
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "text/csv",
          ].includes(contentType)
        ) {
          return setError("sheet files must be xlsx");
        }
        break;

      case "URL":
        return setError("URL document type does not support file upload");

      default:
        return setError("invalid lesson document type");
    }
  } else {
    const isAllowed = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "video/mp4",
      "video/quicktime",
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ].includes(contentType);

    if (!isAllowed) {
      return setError("invalid lesson file format");
    }

    const isVideo = contentType.startsWith("video/");

    if (isVideo && fileSize > MAX_LESSON_SIZE) {
      return setError("video file size must not exceed 1 GB");
    }
  }

  // ============================================================
  // GET VIDEO DURATION
  // ============================================================

  if (contentType.startsWith("video/")) {
    try {
      const durationInSeconds = await getVideoDuration(file);

      // Convert seconds to minutes
      // Math.ceil:
      // 10:00 -> 10
      // 10:01 -> 11
      // 10:59 -> 11
      const durationInMinutes = Math.ceil(durationInSeconds / 60);

      setLessonFormData((prev) => ({
        ...prev,
        duration: {
          ...prev.duration,
          value: durationInMinutes,
          error: "",
        },
      }));
    } catch (error) {
      return setError(
        "Unable to determine video duration. Please select another video.",
      );
    }
  }

  // ============================================================
  // CREATE FILE OBJECT
  // ============================================================

  const newFile = {
    file,
    preview: URL.createObjectURL(file),
    type: file.type,
    name: file.name,
    isExisting: false,
  };

  // ============================================================
  // UPDATE LESSON FILES
  // ============================================================

  setLessonFormData((prev) => ({
    ...prev,
    lessonFiles: {
      ...prev.lessonFiles,
      value: [...prev.lessonFiles.value, newFile],
      error: "",
    },
  }));
};

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleAddFile(file);
  };

  const handleInputFileChange = (e) => {
    const file = e.target.files[0];
    if (file) handleAddFile(file);
    e.target.value = "";
  };

  const handlePreviewFile = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = lessonFormData.lessonFiles.value[0];

    if (!file) return;

    // const source = file.file || file;
    const fileName = file.name || file.filePath?.split("/").pop() || "";
    const extension = fileName.split(".").pop()?.toLowerCase();

    if (file.isExisting && ["xls", "xlsx", "doc", "docx"].includes(extension)) {
      setPreviewData({
        src: `${OFFICE_PREVIEW_URL}${encodeURIComponent(file.filePath)}`,
        width: 0,
        height: 0,
        // fileName: file.filePath.split("?")[0].split("#")[0].split("/").pop(),                //we may require this in future if we want to show the file name in the preview modal
      });
      setLessonUI((prev) => ({
        ...prev,
        previewType: "default",
      }));
      return;
    }

    if (["xls", "xlsx"].includes(extension)) {
      try {
        let buffer;

        if (file.file) {
          buffer = await file.file.arrayBuffer();
        } else {
          const response = await fetch(file.filePath);

          buffer = await response.arrayBuffer();
        }

        const workbook = XLSX.read(buffer, { type: "array" });

        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        const jsonData = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
        });

        setLessonUI((prev) => ({
          ...prev,
          excelData: jsonData,
          previewType: "excel",
        }));
      } catch (error) {
        notify("danger", "Unable to preview Excel file");
      }

      return;
    }

    if (extension === "docx") {
      setLessonUI((prev) => ({ ...prev, previewType: "docx" }));
      return;
    }

    if (extension === "doc") {
      notify("warning", "DOC preview is not supported. Please use DOCX.");
      return;
    }

    setLessonUI((prev) => ({ ...prev, previewType: "default" }));

    setPreviewData({
      src: file.preview || file.filePath,
      width: 0,
      height: 0,
      // fileName:
      //   file.name ||
      //   (file.filePath
      //     ? file.filePath.split("?")[0].split("#")[0].split("/").pop()
      //     : ""),               //we may require this in future if we want to show the file name in the preview modal
    });
  };

  const closePreviewModal = () => {
    setPreviewData({
      src: "",
      width: 0,
      height: 0,
    });
    setLessonUI((prev) => ({
      ...prev,
      previewType: "",
      excelData: [],
    }));
    if (docxContainerRef.current) {
      docxContainerRef.current.innerHTML = "";
    }
  };

  const handleRemoveFileClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setLessonFormData((prev) => ({
      ...prev,
      lessonFiles: {
        value: [],
        error: "",
      },
      duration: {
        value: "",
        oldValue: "",
        error: "",
      }
    }));
  };

  


  const lessonSource =
    activeLanguageTab === "HINDI"
      ? lessonListData?.data?.hindiLessons || []
      : lessonListData?.data?.englishLessons || [];

  const displayedLessons = lessonSource.map((lesson) => ({
    id: lesson.id,
    lessonName: lesson.lesson_name,
    type: lesson.lesson_document_type,
    duration: `${lesson.duration} min`,
    description: lesson.description,
    lessonSeqNo: lesson.lesson_seq_no,
    documents: lesson.documents || [],
    language: lesson.language,
  }));

  const handleDeleteLesson = (lessonId) => {
    showAlert({
      title: "Are you sure you want to delete this lesson?",
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          setShowAlternative((prev) => ({
            ...prev,
            displayedLessonsLoader: true,
          }));
          const res = await deleteLesson(lessonId);

          if (res?.code === 200) {
            notify("success", res?.message || "Lesson deleted successfully");

            await fetchLessons();
            closeAddModal();
          } else {
            const message = Array.isArray(res?.message)
              ? res.message
                  .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
                  .join(", ")
              : res?.message || "Failed to delete lesson";

            notify("danger", message);
          }
        } catch (error) {
          notify("danger", error?.message || "Something went wrong");
        } finally {
          setShowAlternative((prev) => ({
            ...prev,
            displayedLessonsLoader: false,
          }));
        }
      },
    });
  };

  const handleSeqClick = (lesson) => {
    setLessonUI((prev) => ({
      ...prev,
      seqEdit: { id: lesson.id, value: String(lesson.lessonSeqNo ?? "") },
    }));
  };

  const handleSeqChange = (e) => {
    const val = e.target.value;

    if (REGEX.ONLY_DIGITS_REGEX.test(val) && val.length <= 3) {
      setLessonUI((prev) => ({
        ...prev,
        seqEdit: { ...prev.seqEdit, value: val },
      }));
    }
  };

  const handleSeqSave = async (lesson) => {
    const newSeq = Number(lessonUI.seqEdit.value);
    if (
      lessonUI.seqEdit.value === "" ||
      newSeq === lesson.lessonSeqNo ||
      newSeq < 1
    ) {
      setLessonUI((prev) => ({ ...prev, seqEdit: { id: null, value: "" } }));
      return;
    }
    setLessonUI((prev) => ({
      ...prev,
      seqEdit: { id: null, value: "" },
      savingSeqId: lesson.id,
    }));
    try {
      const formData = new FormData();
      formData.append("lessonForm", JSON.stringify({ lessonSeqNo: newSeq }));
      const res = await updateLessonById(lesson.id, formData);
      if (res?.code === 200) {
        notify("success", res?.message || "Sequence number updated");
        await fetchLessons();
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to update sequence number";

        notify("danger", message);
      }
    } catch (err) {
      notify("danger", err?.message || "Something went wrong");
    } finally {
      setLessonUI((prev) => ({ ...prev, savingSeqId: null }));
    }
  };

  const handleSeqKeyDown = (e, lesson) => {
    if (e.key === "Enter") handleSeqSave(lesson);
    if (e.key === "Escape")
      setLessonUI((prev) => ({ ...prev, seqEdit: { id: null, value: "" } }));
  };

  return (
    <>
      <Card className="mb-3">
        <CardHeader
          className={`${hasEnglish && hasHindi ? "lesson-language-tabs" : ""} `}
        >
          <div
            className="d-flex align-items-center cursor"
            onClick={() => toggle("lessons")}
          >
            <i className={`fa fa-chevron-${open.lessons ? "up" : "down"}`} />
            <h5 className="ml-2 mb-0 section-header-title">Lessons</h5>
          </div>
        </CardHeader>

        <Collapse isOpen={open.lessons}>
          {hasEnglish && hasHindi && (
            <Nav tabs className="mb-3">
              <NavItem>
                <NavLink
                  active={activeLanguageTab === "ENGLISH"}
                  onClick={() => setActiveLanguageTab("ENGLISH")}
                  className="cursor"
                >
                  English Lessons
                  <Badge
                    color={
                      activeLanguageTab === "ENGLISH" ? "primary" : "secondary"
                    }
                    className="ml-2"
                    pill
                  >
                    {lessonListData?.data?.englishLessons?.length || 0}
                  </Badge>
                </NavLink>
              </NavItem>

              <NavItem>
                <NavLink
                  active={activeLanguageTab === "HINDI"}
                  onClick={() => setActiveLanguageTab("HINDI")}
                  className="cursor"
                >
                  Hindi Lessons
                  <Badge
                    color={
                      activeLanguageTab === "HINDI" ? "primary" : "secondary"
                    }
                    className="ml-2"
                    pill
                  >
                    {lessonListData?.data?.hindiLessons?.length || 0}
                  </Badge>
                </NavLink>
              </NavItem>
            </Nav>
          )}
          <CardBody>
            <Row>
              <Col md="12">
                {showAlternative.lessonsLoader ? (
                  <div className="lessonsLoaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2x" />
                  </div>
                ) : displayedLessons?.length === 0 ? (
                  <div className="lesson-empty-state">
                    <div className="lesson-empty-state-icon">
                      <i className="fa fa-book-open" />
                    </div>
                    <p className="lesson-empty-state-title">No lessons yet</p>
                    <p className="lesson-empty-state-subtitle">
                      Click add lesson to get started.
                    </p>
                  </div>
                ) : (
                  displayedLessons.map((lesson, index) => (
                    <div
                      key={lesson.id}
                      className="lesson-timeline-item d-flex"
                    >
                      {/* left timeline index */}
                      <div className="timeline-left">
                        {lessonUI.savingSeqId === lesson.id ? (
                          <>
                            <div
                              className={`timeline-number timeline-bg-${LessonTypeColor[lesson.type]} text-${LessonTypeColor[lesson.type]}`}
                            >
                              <i className="fa fa-spinner fa-spin" />
                            </div>
                          </>
                        ) : lessonUI.seqEdit.id === lesson.id ? (
                          <>
                            <input
                              type="text"
                              inputMode="numeric"
                              className="seq-edit-input"
                              value={lessonUI.seqEdit.value}
                              autoFocus
                              onChange={handleSeqChange}
                              onBlur={() => handleSeqSave(lesson)}
                              onKeyDown={(e) => handleSeqKeyDown(e, lesson)}
                            />
                          </>
                        ) : (
                          <>
                            <div
                              className={`timeline-number timeline-number--editable timeline-bg-${LessonTypeColor[lesson.type]} text-${LessonTypeColor[lesson.type]}`}
                              title="Click to edit sequence number"
                              onClick={() => handleSeqClick(lesson)}
                            >
                              {lesson.lessonSeqNo}
                              <i className="fa fa-pencil seq-edit-hint" />
                            </div>
                          </>
                        )}

                        {index !== displayedLessons.length - 1 && (
                          <div className="timeline-line"></div>
                        )}
                      </div>

                      <Card className="lesson-card mb-3 p-3 w-75">
                        <div className="d-flex justify-content-between align-items-start">
                          {/* lesson content */}
                          <div className="w-100 mr--4">
                            <div className="d-flex align-items-start justify-content-between w-100">
                              <div className="lesson-title mr-3">
                                {lesson.lessonName}
                              </div>

                              <div className="d-flex align-items-center flex-shrink-0 mt--2 mb--1">
                                <i
                                  id={`lessonIcon-${lesson.id}`}
                                  className={`fa ${LESSON_TYPE_META[lesson.type]?.icon || "fa fa-file-pdf"} fa-lg lesson-icon cursor text-blue mr--2`}
                                  onClick={() => openLessonPreview(lesson)}
                                />

                                <UncontrolledPopover
                                  placement="top"
                                  target={`lessonIcon-${lesson.id}`}
                                  trigger="hover"
                                  className="shadow-lg border-0"
                                  container="body"
                                >
                                  <PopoverBody>
                                    Click icon to preview lesson
                                  </PopoverBody>
                                </UncontrolledPopover>

                                <span className="text-muted">
                                  {lesson.duration}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* edit button */}
                          <Button
                            type="button"
                            color="link"
                            size="sm"
                            className="lesson-edit-btn edit-btn text-primary mr--5"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleEditLesson(lesson);
                            }}
                          >
                            <i className="fa fa-xl fa-edit mr-1 mt-2" />
                          </Button>

                          <Button
                            type="button"
                            color="link"
                            size="sm"
                            className="lesson-edit-btn delete-btn text-danger mr--1"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteLesson(lesson.id);
                            }}
                          >
                            <i className="fa fa-xl fa-trash mr-1 mt-2" />
                          </Button>
                        </div>
                        <hr className="my-3" />

                        {/* description */}
                        {(() => {
                          const sanitized = DOMPurify.sanitize(
                            lesson.description,
                          );
                          const el = document.createElement("div");
                          el.innerHTML = sanitized;
                          const plainText = el.textContent || "";

                          const isExpanded = !!expandedDescriptions[lesson.id];
                          const isLong = plainText.length > PREVIEW_LIMIT;

                          const truncateHtml = (html, limit) => {
                            // Create a temporary container to parse the HTML string into DOM nodes
                            const container = document.createElement("div");
                            container.innerHTML = html;

                            // Keeps track of the number of text characters processed
                            let count = 0;

                            // Recursively traverse all nodes in the HTML
                            const walk = (node) => {
                              // If the character limit is already reached,
                              // remove this node and all remaining sibling nodes
                              if (count >= limit) {
                                node.parentNode?.removeChild(node);
                                return;
                              }

                              // Process text nodes
                              if (node.nodeType === Node.TEXT_NODE) {
                                // Calculate how many more characters can be displayed
                                const remaining = limit - count;

                                // If this text exceeds the remaining limit,
                                // truncate it and append "..."
                                if (node.textContent.length > remaining) {
                                  node.textContent =
                                    node.textContent.slice(0, remaining) +
                                    "...";
                                  count = limit;
                                } else {
                                  // Otherwise, count the entire text and continue
                                  count += node.textContent.length;
                                }
                              }
                              // Process element nodes (e.g., <p>, <strong>, <ul>, etc.)
                              else if (node.nodeType === Node.ELEMENT_NODE) {
                                // Copy child nodes to an array because nodes may be removed during traversal
                                const children = Array.from(node.childNodes);

                                // Recursively process each child node
                                children.forEach(walk);
                              }
                            };

                            // Start traversing from the container's top-level child nodes
                            Array.from(container.childNodes).forEach(walk);

                            // Return the truncated HTML while preserving valid HTML structure
                            return container.innerHTML;
                          };

                          if (!isLong) {
                            return (
                              <div
                                className="lesson-description-content mb-0"
                                dangerouslySetInnerHTML={{
                                  __html: sanitized,
                                }}
                              />
                            );
                          }

                          if (isExpanded) {
                            return (
                              <>
                                <div
                                  className="lesson-description-content mb-0"
                                  dangerouslySetInnerHTML={{
                                    __html: sanitized,
                                  }}
                                />
                                <div className="text-center mt-2 w-100">
                                  <button
                                    className="lesson-read-more-btn"
                                    onClick={() =>
                                      setExpandedDescriptions((prev) => ({
                                        ...prev,
                                        [lesson.id]: false,
                                      }))
                                    }
                                  >
                                    Read Less
                                  </button>
                                </div>
                              </>
                            );
                          }

                          return (
                            <>
                              <div
                                className="lesson-description-content mb-0"
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(
                                    truncateHtml(sanitized, PREVIEW_LIMIT),
                                  ),
                                }}
                              />
                              <div className="text-center mt-2 w-100">
                                <button
                                  className="lesson-read-more-btn"
                                  onClick={() =>
                                    setExpandedDescriptions((prev) => ({
                                      ...prev,
                                      [lesson.id]: true,
                                    }))
                                  }
                                >
                                  Read More
                                </button>
                              </div>
                            </>
                          );
                        })()}
                      </Card>
                    </div>
                  ))
                )}
                <div className="d-flex justify-content-start">
                  <div className="add-more-file-wrapper add-more-file-wrapper--deep-blue">
                    +
                  </div>
                  <Button
                    color="primary"
                    size="sm"
                    className="btn-deep-blue ml-2"
                    onClick={openAddModal}
                  >
                    Add Lesson
                  </Button>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Collapse>
      </Card>
      <RightPanelCard
        isOpen={isModalOpen !== null}
        onClose={closeAddModal}
        fullHeight
        enableScroll={true}
      >
        <Card className="location-card  mb-0">
          <CardHeader tag="div" className="p-3">
            <div className="d-flex align-items-center justify-content-between mt-2">
              <div className="d-flex align-items-center">
                <i
                  className="fa-solid fa-angle-left cursor mr-2"
                  onClick={closeAddModal}
                ></i>
                <h4 className="mb-0">
                  {isModalOpen === "edit" ? "Edit Lesson" : "Add Lesson"}
                </h4>
              </div>

              <div className="d-flex justify-content-end">
                <i
                  className="fa-solid fa-xmark cursor text-muted"
                  onClick={closeAddModal}
                ></i>
              </div>
            </div>
          </CardHeader>

          <CardBody className="scrollbar-inner overflow-auto">
            {showAlternative.loader ? (
              <div className="loaderContainer">
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </div>
            ) : showAlternative.error ? (
              <div className="loaderContainer text-muted d-flex flex-column">
                <span className="text-danger">{showAlternative.error}</span>
              </div>
            ) : (
              <Form>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Title</Label>
                      <span className="text-danger"> *</span>
                      <Input
                        name="lessonName"
                        type="text"
                        placeholder={
                          isHindi
                            ? "पाठ का शीर्षक दर्ज करें"
                            : "Enter lesson title"
                        }
                        value={lessonFormData.lessonName.value}
                        onChange={(e) =>
                          handleInputChange("lessonName", e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        maxLength={100}
                        className={
                          lessonFormData.lessonName.error ? "is-invalid" : ""
                        }
                      />
                      {lessonFormData.lessonName.error && (
                        <div className="invalid-feedback d-block">
                          {lessonFormData.lessonName.error}
                        </div>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="6">
                    <FormGroup>
                      <Label>Content Sequence</Label>
                      <span className="text-danger"> *</span>
                      <Input
                        name="lessonSeqNo"
                        type="text"
                        maxLength={3}
                        min="1"
                        placeholder={"Enter content sequence eg: 1"}
                        value={lessonFormData.lessonSeqNo.value}
                        onChange={(e) =>
                          handleInputChange("lessonSeqNo", e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur("lessonSeqNo", e.target.value)
                        }
                        disabled={isModalOpen === "add"}
                        className={
                          lessonFormData.lessonSeqNo.error ? "is-invalid" : ""
                        }
                      />
                      {lessonFormData.lessonSeqNo.error && (
                        <div className="invalid-feedback d-block">
                          {lessonFormData.lessonSeqNo.error}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Select File Type</Label>
                      <span className="text-danger"> *</span>
                      <Select
                        options={COURSE_TYPE}
                        placeholder={"Select file type"}
                        isDisabled={courseType === "RECORDED"}
                        styles={SELECT_CUSTOM_STYLES}
                        classNamePrefix="react-select"
                        isClearable={courseType !== "RECORDED"}
                        value={
                          COURSE_TYPE.find(
                            (option) =>
                              option.value ===
                              lessonFormData.lessonDocumentType.value,
                          ) || null
                        }
                        onChange={(selected) =>
                          handleInputChange(
                            "lessonDocumentType",
                            selected ? selected.value : "",
                          )
                        }
                        className={
                          lessonFormData.lessonDocumentType.error
                            ? "is-invalid"
                            : ""
                        }
                        onBlur={(e) =>
                          handleOnBlur(
                            "lessonDocumentType",
                            lessonFormData.lessonDocumentType.value,
                          )
                        }
                        menuPortalTarget={document.body}
                        menuPosition="absolute"
                      />
                      {lessonFormData.lessonDocumentType.error && (
                        <div className="invalid-feedback d-block">
                          {lessonFormData.lessonDocumentType.error}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Duration (Minutes)</Label>
                      <span className="text-danger"> *</span>
                      <Input
                          name="duration"
                          type="text"
                          inputMode="numeric"
                          maxLength={3}
                          placeholder="Enter lesson duration in minutes"
                          disabled={courseType === "RECORDED" || lessonFormData.lessonDocumentType?.value === "VIDEO"}
                          value={lessonFormData.duration.value}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (REGEX?.ONLY_DIGITS_REGEX.test(value)) {
                              handleInputChange("duration", value);
                            }
                          }}
                          onBlur={(e) =>
                            handleOnBlur("duration", e.target.value)
                          }
                          className={
                            lessonFormData.duration.error ? "is-invalid" : ""
                          }
                        />
                      {lessonFormData.duration.error && (
                        <div className="invalid-feedback d-block">
                          {lessonFormData.duration.error}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label>Description</Label>
                      <span className="text-danger"> *</span>
                      <div
                        className={`lesson-description-quill quill-wrapper ${
                          lessonFormData.description.error
                            ? "quill-invalid"
                            : ""
                        }`}
                      >
                        <ReactQuill
                              ref={handleQuillRef}
                              theme="snow"
                              modules={MODULES}
                              placeholder={isHindi ? "विवरण दर्ज करें" : "Enter description"}
                              value={lessonFormData.description.value || ""}
                              onChange={(value, delta, source, editor) => {
                                setLessonFormData((prev) => ({
                                  ...prev,
                                  description: {
                                    ...prev.description,
                                    value,
                                    error: "",
                                  },
                                }));

                                // Update the counter directly from the change event —
                                // don't rely only on the separate "text-change" listener.
                                setDescriptionLength(getQuillPlainLength(editor));
                              }}
                              onBlur={() => {
                                const value = lessonFormData.description.value || "";
                                const error = getValidationError("description", value);
                                setLessonFormData((prev) => ({
                                  ...prev,
                                  description: { ...prev.description, error },
                                }));
                              }}
                              className={lessonFormData.description.error ? "is-invalid" : ""}
                            />
                        <div className="d-flex justify-content-between align-items-center mt-1">
                          <div className="invalid-feedback d-block m-0">
                            {lessonFormData.description.error || ""}
                          </div>

                          {(() => {
                              const el = document.createElement("div");
                              el.innerHTML = lessonFormData.description.value || "";

                              // const len = el.textContent.replace(/\n$/, "").length;

                              return (
                                <div className="d-flex justify-content-between align-items-center mt-1">
                                  <small
                                    className={
                                      descriptionLength >= MAX_DESCRIPTION_LENGTH
                                        ? "text-danger"
                                        : "text-muted"
                                    }
                                  >
                                    {descriptionLength}/{MAX_DESCRIPTION_LENGTH}
                                  </small>
                                </div>
                              );
                            })()}
                        </div>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="12">
                    {/* File Upload */}
                    <FormGroup className="d-flex flex-column align-items-start w-100">
                      <Label>
                        Upload File <span className="text-danger"> *</span>
                      </Label>

                      <div
                        className={`thumbnail-upload-card ${
                          lessonFormData.lessonFiles.error
                            ? "profile-invalid"
                            : ""
                        }`}
                        onDragOver={(e) => e.preventDefault()}
                        onDragEnter={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                      >
                        <div className="thumbnail-upload-label mb-0">
                          {lessonFormData.lessonFiles.value.length > 0 ? (
                            <div className="course-media-preview-container mt--3">
                              <div className="course-media-preview-wrapper">
                                <span
                                  className="course-remove-icon"
                                  onClick={handleRemoveFileClick}
                                >
                                  &times;
                                </span>

                                {(() => {
                                  const file =
                                    lessonFormData.lessonFiles.value[0];
                                  const src = file?.preview || file?.filePath;
                                  const mimeType = file?.type || "";
                                  const isImage =
                                    mimeType.startsWith("image/") ||
                                    /\.(png|jpg|jpeg)$/i.test(src);
                                  const isVideo =
                                    mimeType.startsWith("video/") ||
                                    /\.(mp4|webm|ogg|mov|m4v)$/i.test(src);

                                  if (isImage) {
                                    return (
                                      <img
                                        src={src}
                                        alt="lesson-file"
                                        className="lesson-thumbnail-preview mb--3 cursor"
                                        onClick={handlePreviewFile}
                                      />
                                    );
                                  }
                                  if (isVideo) {
                                    return (
                                      <video
                                        src={src}
                                        className="lesson-thumbnail-preview mb--3 cursor"
                                        controls
                                        onClick={handlePreviewFile}
                                      />
                                    );
                                  }
                                  const fileName =
                                    file?.name ||
                                    src?.split("/").pop() ||
                                    "Document";
                                  const { icon, color } = getFileIcon(fileName);

                                  return (
                                    <div
                                      className="lesson-pdf-placeholder cursor mb--3"
                                      onClick={handlePreviewFile}
                                    >
                                      <i
                                        className={`fa ${icon} lesson-pdf-icon ${color}`}
                                      />
                                      <span className="lesson-pdf-name">
                                        {fileName}
                                      </span>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          ) : (
                            <div className="d-flex flex-column align-items-center">
                              <i className="fa fa-cloud-upload fa-2x muted-label" />

                              {lessonFormData.lessonDocumentType.value ? (
                                <p className="mb--1 mt-1 muted-label font-size-sm">
                                  (Accepted formats:{" "}
                                  {ACCEPTED_FORMATS_MAP[
                                    lessonFormData.lessonDocumentType.value
                                  ]?.label || "All files"}
                                  {" - Max 1 GB"})
                                </p>
                              ) : (
                                <p className="mb-0 mt-1 muted-label font-size-sm">
                                  (Max size: 1 GB)
                                </p>
                              )}

                              <p className="mb-1 muted-label font-size-sm">
                                Click to upload file
                              </p>

                              <label
                                htmlFor="lessonFile"
                                className="choose-image-btn btn btn-simple btn-sm m-0"
                              >
                                Choose File
                              </label>
                            </div>
                          )}
                        </div>

                        <Input
                          type="file"
                          name="image"
                          accept={
                            lessonFormData.lessonDocumentType.value
                              ? ACCEPTED_FORMATS_MAP[
                                  lessonFormData.lessonDocumentType.value
                                ]?.accept ||
                                "image/*, application/pdf, video/mp4, video/quicktime, application/vnd.ms-powerpoint"
                              : "image/*, application/pdf, video/mp4, video/quicktime, application/vnd.ms-powerpoint"
                          }
                          onChange={handleInputFileChange}
                          className="d-none"
                          id="lessonFile"
                        />
                      </div>
                      {lessonFormData.lessonFiles.error && (
                        <div className="invalid-feedback d-block">
                          {lessonFormData.lessonFiles.error}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </Form>
            )}
          </CardBody>
          <CardFooter className="text-right">
            <Button
              color="secondary"
              type="button"
              className="save-btn-sm"
              onClick={closeAddModal}
            >
              Cancel
            </Button>

            <Button
              color="deep-blue"
              className="save-btn-sm"
              onClick={handleSave}
              disabled={showAlternative.displayedLessonsLoader}
            >
              {showAlternative.displayedLessonsLoader ? (
                <>
                  <i className="fa fa-spinner fa-spin mr-2" />
                </>
              ) : (
                "Save"
              )}
            </Button>
          </CardFooter>
        </Card>
      </RightPanelCard>

      <Modal
        isOpen={
          !!previewData.src ||
          lessonUI.previewType === "excel" ||
          lessonUI.previewType === "docx"
        }
        toggle={closePreviewModal}
        size="xl"
        centered
      >
        <ModalHeader toggle={closePreviewModal}>Document Preview</ModalHeader>

        <ModalBody className="text-center">
          {lessonUI.previewType === "excel" ? (
            <div className="lesson-preview-excel">
              <table className="table table-bordered">
                <tbody>
                  {lessonUI.excelData.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : lessonUI.previewType === "docx" ? (
            <div ref={docxContainerRef} className="lesson-preview-docx" />
          ) : /\.(png|jpg|jpeg)$/i.test(previewData.src) ? (
            <img
              src={previewData.src}
              alt="Preview"
              className="lesson-preview-image"
            />
          ) : /\.(mp4|webm|ogg|mov|m4v)$/i.test(previewData.src) ? (
            <video
              src={previewData.src}
              controls
              className="lesson-preview-video"
            />
          ) : (
            <iframe
              src={previewData.src}
              title="Document Preview"
              className="lesson-preview-iframe"
            />
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default LessonsSection;
